import * as React from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { Form as RouterForm, redirect, useActionData, useLoaderData, useNavigation, useParams } from 'react-router-dom';

import { completePasswordReset, validatePasswordReset } from '@/helper/apicalls';
import { formatErrors } from '@/helper/utility';

function getPasswordError(password: string) {
  if (password.length < 10) return 'Password must be at least 10 characters';
  if (/\s/.test(password)) return 'Password cannot contain spaces';
  return '';
}

export async function resetPasswordLoader({ params }: any) {
  const response = await validatePasswordReset(params.token);
  // eslint-disable-next-line unicorn/no-null
  return response?.status == 200 ? null : response;
}

export async function resetPasswordAction({ request, params }: any) {
  const body = Object.fromEntries(await request.formData());
  const password = body.password?.toString() || '';
  const confirmPassword = body.confirmPassword?.toString() || '';

  // eslint-disable-next-line security/detect-possible-timing-attacks
  if (password !== confirmPassword) {
    return [{ path: 'confirmPassword', msg: 'Passwords must match' }];
  }

  const response = await completePasswordReset(params.token, body);
  return response?.status == 200 ? redirect('/login?passwordReset=success') : response;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function ResetPassword() {
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordTouched, setPasswordTouched] = React.useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = React.useState(false);
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = React.useState(false);

  const { token } = useParams();
  const loaderError = useLoaderData() as string | null;
  const actionData = useActionData() as any;
  const navigation = useNavigation();
  const isLoading = navigation.state == 'submitting';
  const serverErrors = Array.isArray(actionData) ? formatErrors(actionData) : {};
  const passwordError = getPasswordError(password);
  const isPasswordValid = password.length > 0 && !passwordError;
  const confirmPasswordError =
    confirmPasswordTouched && isPasswordValid && password !== confirmPassword ? 'Passwords must match' : '';
  const errors: Record<string, string> = { ...serverErrors };
  if (passwordTouched && passwordError) errors.password = passwordError;
  if (confirmPasswordTouched) {
    if (confirmPasswordError) {
      errors.confirmPassword = confirmPasswordError;
    } else {
      delete errors.confirmPassword;
    }
  }
  const isSubmitDisabled = isLoading || !token || !isPasswordValid || password !== confirmPassword;
  const message =
    actionData && !Array.isArray(actionData)
      ? typeof actionData === 'string'
        ? actionData
        : actionData.message || 'There is a problem with the server, contact your administrator.'
      : '';

  return (
    <Container fluid>
      <Row className='mt-5'>
        <Col className='login ms-auto me-auto'>
          <Card>
            <Card.Header>Reset Password</Card.Header>
            <Card.Body>
              {loaderError ? (
                <Alert variant='danger'>{loaderError}</Alert>
              ) : (
                <Form as={RouterForm} method='post' noValidate>
                  {message && <Alert variant='danger'>{message}</Alert>}
                  <Form.Group className='mb-3 position-relative' controlId='formBasicPassword'>
                    <Form.Label className='visually-hidden'>Password</Form.Label>
                    <Form.Control
                      type={passwordVisible ? 'text' : 'password'}
                      placeholder='New Password'
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      onBlur={() => setPasswordTouched(true)}
                      isInvalid={errors?.password ? true : false}
                      disabled={isLoading}
                      name='password'
                      autoComplete='new-password'
                    />
                    {passwordVisible ? (
                      <Eye className='icon-input' onClick={() => setPasswordVisible(false)} />
                    ) : (
                      <EyeSlash className='icon-input' onClick={() => setPasswordVisible(true)} />
                    )}
                    <Form.Control.Feedback type='invalid'>{errors?.password}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className='mb-3 position-relative' controlId='formBasicConfirmPassword'>
                    <Form.Label className='visually-hidden'>Confirm Password</Form.Label>
                    <Form.Control
                      type={confirmPasswordVisible ? 'text' : 'password'}
                      placeholder='Confirm Password'
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      onBlur={() => setConfirmPasswordTouched(true)}
                      isInvalid={errors?.confirmPassword ? true : false}
                      disabled={isLoading || !isPasswordValid}
                      name='confirmPassword'
                      autoComplete='new-password'
                    />
                    {confirmPasswordVisible ? (
                      <Eye className='icon-input' onClick={() => setConfirmPasswordVisible(false)} />
                    ) : (
                      <EyeSlash className='icon-input' onClick={() => setConfirmPasswordVisible(true)} />
                    )}
                    <Form.Control.Feedback type='invalid'>{errors?.confirmPassword}</Form.Control.Feedback>
                  </Form.Group>
                  <Button className='w-100' variant='primary' type='submit' disabled={isSubmitDisabled}>
                    {isLoading ? (
                      <React.Fragment>
                        <Spinner as='span' size='sm' role='status' aria-hidden='true' />
                        &nbsp;Saving...
                      </React.Fragment>
                    ) : (
                      'Set Password'
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
